import { useEffect, useRef, useState } from 'react';

export default function HowItWorks() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      number: '01',
      title: 'Connect Your Property',
      description: 'Sync your booking calendars from Airbnb, Vrbo, and other platforms in seconds.',
      icon: 'ri-link',
      image: 'https://public.readdy.ai/ai/img_res/114167e7704b94596e4e7625a7d6683a.jpg'
    },
    {
      number: '02',
      title: 'Automate Scheduling',
      description: 'CleanerConnect assigns cleaners automatically based on availability and property location.',
      icon: 'ri-robot-line',
      image: 'https://readdy.ai/api/search-image?query=Automated%20scheduling%20system%20interface%20showing%20cleaner%20assignments%20and%20availability%20calendar%2C%20modern%20app%20dashboard%20with%20teal%20and%20blue%20colors%2C%20professional%20UI%20design%20displaying%20automatic%20task%20distribution%2C%20clean%20minimalist%20aesthetic%20with%20focus%20on%20automation%20and%20efficiency&width=600&height=400&seq=step-2-automate&orientation=landscape'
    },
    {
      number: '03',
      title: 'Review and Relax',
      description: 'Get real-time notifications and review completed work with photo verification.',
      icon: 'ri-checkbox-circle-line',
      image: 'https://readdy.ai/api/search-image?query=Mobile%20notification%20interface%20showing%20completed%20cleaning%20job%20with%20before%20and%20after%20photos%2C%20professional%20app%20design%20with%20teal%20accent%20colors%2C%20clean%20modern%20UI%20displaying%20job%20completion%20status%20and%20review%20system%2C%20minimalist%20style%20with%20emphasis%20on%20quality%20assurance%20and%20peace%20of%20mind&width=600&height=400&seq=step-3-review&orientation=landscape'
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-br from-teal-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <h2 className="text-5xl font-bold text-teal-900 mb-4">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get started in three simple steps
          </p>
        </div>

        <div className="space-y-24">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`transition-all duration-1000 transform ${
                isVisible
                  ? 'translate-x-0 opacity-100'
                  : index % 2 === 0 ? '-translate-x-10 opacity-0' : 'translate-x-10 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
                <div className="flex-1">
                  <div className="relative">
                    <div className="text-8xl font-bold text-teal-200 absolute -top-8 -left-4">
                      {step.number}
                    </div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                          <i className={`${step.icon} text-white text-3xl`}></i>
                        </div>
                        <h3 className="text-4xl font-bold text-teal-900">
                          {step.title}
                        </h3>
                      </div>
                      <p className="text-xl text-gray-700 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex-1">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-80 object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-teal-900/20 to-transparent"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
